/**
 * ds-band-thresholds.js — Deal Screen band threshold single source of truth (frontend).
 *
 * Authority: backend/core/system/constants.py :: DEAL_SCREEN_BAND_SCORE_RANGES
 * (DealScreenBands.NO_SIGNAL: (0,40), WEAK: (41,69), STRONG: (70,100))
 *
 * WI-DS-RENDER-NAME-001 (defect E): resolveOverallScore() previously hardcoded 40/70
 * inline. This module mirrors the backend constant so there is ONE place to update if
 * the band boundaries ever change. Import DS_BAND_THRESHOLDS here instead of
 * writing literal numbers inside components.
 *
 * Reader vocabulary: STRONG / WEAK. The reader band word IS the internal band
 * word — internal WEAK renders as WEAK (NOT a kinder "MODERATE"), internal
 * NO_SIGNAL collapses to WEAK (the reader never sees the NO_SIGNAL token).
 * Authority for this alias: DEAL_SCREEN_PROSE_STYLE.md §9 band_vocabulary
 * (alias_internal_to_reader: STRONG->STRONG, WEAK->WEAK, NO_SIGNAL->WEAK).
 * Internal band enum (persisted): STRONG / WEAK / NO_SIGNAL
 */

/** Minimum score (inclusive) for the STRONG band. */
export const DS_BAND_STRONG_MIN = 70;

/** Minimum score (inclusive) for the WEAK band. */
export const DS_BAND_WEAK_MIN = 41;

/**
 * Map a band_score → internal band enum (mirrors backend DEAL_SCREEN_BAND_SCORE_RANGES).
 *   STRONG    : score >= DS_BAND_STRONG_MIN (70)
 *   WEAK      : score in DS_BAND_WEAK_MIN..DS_BAND_STRONG_MIN-1 (41-69)
 *   NO_SIGNAL : score < DS_BAND_WEAK_MIN (0-40)
 * For the reader-facing word, pass the result through internalBandToReaderLabel().
 */
export function scoreToBand(score) {
  if (score == null) return 'NO_SIGNAL';
  if (score >= DS_BAND_STRONG_MIN) return 'STRONG';
  if (score >= DS_BAND_WEAK_MIN) return 'WEAK';
  return 'NO_SIGNAL';
}

/**
 * Map internal band enum value → reader-vocabulary label.
 *
 * WI-C (band truth): the reader word IS the stored band word — STRONG renders as
 * STRONG, WEAK renders as WEAK. The prior `WEAK -> 'MODERATE'` was display drift
 * from this module's OWN documented authority (DEAL_SCREEN_PROSE_STYLE.md §9
 * alias_internal_to_reader: WEAK->WEAK); it softened the tile so it contradicted
 * the prose ("lower WEAK band") and the verdict ("Not Worth a Call").
 * Internal NO_SIGNAL collapses to WEAK (the reader never sees NO_SIGNAL), per the
 * same §9 alias (NO_SIGNAL->WEAK).
 */
export function internalBandToReaderLabel(band) {
  if (band === 'STRONG') return 'STRONG';
  if (band === 'WEAK') return 'WEAK';
  // NO_SIGNAL or any unknown → reader sees WEAK (never the internal NO_SIGNAL token)
  return 'WEAK';
}

/**
 * Full threshold object for tests and documentation.
 * Mirrors backend DEAL_SCREEN_BAND_SCORE_RANGES.
 */
export const DS_BAND_THRESHOLDS = {
  STRONG: { min: DS_BAND_STRONG_MIN, max: 100 },
  WEAK:   { min: DS_BAND_WEAK_MIN,   max: DS_BAND_STRONG_MIN - 1 },
  NO_SIGNAL: { min: 0, max: DS_BAND_WEAK_MIN - 1 },
};

/**
 * Band word map — the reader-facing tile heading for each internal band enum.
 *
 * The displayed word is the on-enum reader vocabulary {STRONG, WEAK} — the SAME
 * vocabulary internalBandToReaderLabel() returns and the contract enum
 * (deal_screen_output_contract.json reader_band_vocabulary_single: ["STRONG","WEAK"]).
 * The friendlier "Strong Signal"/"Weak Signal" phrasing was display drift: it made the
 * ROW-3 tiles read "Weak Signal" while the expanded detail read "WEAK" (CEO flagged this
 * on the live Evamp screen; Evamp output-quality council, B1). NO_SIGNAL aliases to WEAK —
 * the reader never sees NO_SIGNAL.
 *
 * resolveBandWord() in DealScreenDetailView.jsx delegates to internalBandToReaderLabel()
 * directly; this map is kept on-enum so any consumer reading it cannot reintroduce the drift.
 */
export const BAND_WORD_LABELS = {
  STRONG:    'STRONG',
  WEAK:      'WEAK',
  NO_SIGNAL: 'WEAK',
};
